/**
 * This class can be used with the Bootstrap 4 form file uploader.
 *
 * When rendering the form:
 * add the **`data-event-file`** attribute to the main div including the input and label.
 * In Symfony we can do this by adding **`{'row_attr': {'data-event-file': ''}}`**
 * to the **`form_row()`** function.
 */
export class FileUpload {
  constructor() {
    // Max file size in bytes (50 MB). This is to prevent nginx denying the request.
    this.maxFileSize = 52428800;
    this.fileElements = undefined;
    this.hasError = false;

    // Build defaults
    this.errorBlockMainClass = 'js-invalid';
    this.errorBlockClasses = [this.errorBlockMainClass, 'invalid-feedback', 'd-block'];
    this.errorMessageClasses = ['form-error-message'];
  }

  /**
   * Binds all the events
   */
  bindEventListeners() {
    this.bindValidationListener();
    this.bindValueListener();
  }

  /**
   * Binds the ValueListener (see `updateValue()`) on `change`. Also executes on initialization.
   *
   * @param {Boolean} cacheFileElements When true, **`data-event-file`** will be cached.
   */
  bindValueListener(cacheFileElements = true) {
    this.getFileElements(cacheFileElements).forEach((fileElement) => {
      const input = fileElement.querySelector('input[type=file]');

      this.updateValue(input);
      input.addEventListener('change', ({target}) => this.updateValue(target));
    });
  }

  /**
   * Binds the ValidationListener (see `validate()`) on `change`. Also executes on initialization.
   *
   * @param {Boolean} cacheFileElements When true, **`data-event-file`** will be cached.
   */
  bindValidationListener(cacheFileElements = true) {
    this.getFileElements(cacheFileElements).forEach((fileElement) => {
      const input = fileElement.querySelector('input[type=file]');

      this.validate(input);
      input.addEventListener('change', ({target}) => this.validate(target));
    });
  }

  /**
   * Sets the input value to Formatted string of all the fileNames that are selected.
   *
   * @param {HTMLInputElement} input
   */
  updateValue(input) {
    const fileNames = [];

    for (let i = 0; i < input.files.length; i++) {
      fileNames.push(input.files.item(i).name);
    }

    if (0 < fileNames.length) {
      input.parentElement.querySelector('.custom-file-label').innerText = fileNames.join(', ');
    }
  }

  /**
   * Checks if input is valid & shows error if needed.
   *
   * @param {HTMLInputElement} input
   */
  validate(input) {
    let errors = 0;

    for (let i = 0; i < input.files.length; i++) {
      if (input.files[i].size > this.maxFileSize) {
        const inputSizeMb = ((input.files[i].size / 1024) / 1024).toFixed(2);
        const maxSizeMb = ((this.maxFileSize / 1024) / 1024).toFixed(2);

        if (false === this.hasError) {
          this.showError(input, `Het bestand is te groot ${inputSizeMb} MB. De maximale toegestane grootte is ${maxSizeMb} MB`);
        }

        input.value = '';
        errors++;
      }
    }

    if (1 > errors && true === this.hasError) {
      this.hideError(input);
    }
  }

  /**
   * @param {Boolean} cached
   *
   * @return {NodeListOf<Element>}
   */
  getFileElements(cached = true) {
    if (true === cached) {
      if (undefined === this.fileElements) {
        this.fileElements = document.querySelectorAll('[data-event-file]');
      }

      return this.fileElements;
    }

    return document.querySelectorAll('[data-event-file]');
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  showError(input, message) {
    input.parentElement.append(this.buildError(message));

    this.hasError = true;
  }

  /**
   * @param {HTMLInputElement} input
   */
  hideError(input) {
    input.parentElement.querySelector(`.${this.errorBlockMainClass}`).remove();
    this.hasError = false;
  }

  /**
   * @param {string} message
   *
   * @return HTMLElement
   */
  buildError(message) {
    const blockEl = document.createElement('span');
    blockEl.className = this.errorBlockClasses.join(' ');

    const messageEl = document.createElement('span');
    messageEl.className = this.errorMessageClasses.join(' ');
    messageEl.innerText = message;
    blockEl.append(messageEl);

    return blockEl;
  }
}

export default new FileUpload();
